/**
 * Builds the inner `data` object for `trained_to` on POST /training-log/create.
 * Document.js wraps the result as `{ tree_node_id, data: <this> }`.
 * Includes full step/substep/child node trees (short_description lives under detail, etc.).
 * Each top-level step in `steps` includes `status`: "Trained" if the revision header checkbox
 * was selected (procedure scope), otherwise "Partially Trained" (one or more step checkboxes).
 * Each step includes `is_complete`: true for steps confirmed in this submission; false for others.
 */

const compareSiblingOrders = (a, b) => {
  const intA = parseInt(a, 10);
  const intB = parseInt(b, 10);
  if (Number.isNaN(intA) && Number.isNaN(intB)) return 0;
  if (Number.isNaN(intA)) return 1;
  if (Number.isNaN(intB)) return -1;
  return intA - intB;
};

const sortSections = (nodes) =>
  [...(nodes || [])].sort((a, b) => {
    const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
    const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
    const orderCompare = compareSiblingOrders(orderA, orderB);
    if (orderCompare !== 0) return orderCompare;
    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
  });

const sortSteps = (steps) =>
  [...(steps || [])].sort((a, b) =>
    compareSiblingOrders(a.sibling_order, b.sibling_order),
  );

/** Recursively sort substeps and rich-text/video/etc. children by sibling_order for stable payloads. */
export const sortNodeHierarchyOrders = (node) => {
  if (!node || typeof node !== "object") return;
  if (Array.isArray(node.substeps)) {
    node.substeps.sort((a, b) =>
      compareSiblingOrders(a.sibling_order, b.sibling_order),
    );
    node.substeps.forEach(sortNodeHierarchyOrders);
  }
  if (Array.isArray(node.children)) {
    node.children.sort((a, b) =>
      compareSiblingOrders(a.sibling_order, b.sibling_order),
    );
    node.children.forEach(sortNodeHierarchyOrders);
  }
};

const deepCloneNode = (node) => JSON.parse(JSON.stringify(node));

/** Revision header (procedure) checked → full procedure trained; step-only → partial. */
const STEP_STATUS = {
  TRAINED: "Trained",
  PARTIALLY_TRAINED: "Partially Trained",
};

const applyStepStatus = (stepClone, status) => {
  if (!stepClone || typeof stepClone !== "object") return;
  stepClone.status = status;
};

/** Recursively set `is_complete` on step substeps / rich-text children (procedure = full tree). */
const setIsCompleteDeep = (node, value) => {
  if (!node || typeof node !== "object") return;
  node.is_complete = value;
  if (Array.isArray(node.substeps)) {
    for (const sub of node.substeps) {
      setIsCompleteDeep(sub, value);
    }
  }
  if (Array.isArray(node.children)) {
    for (const ch of node.children) {
      setIsCompleteDeep(ch, value);
    }
  }
};

export function findStepByTreeNodeId(allNodesData, treeNodeId) {
  if (!treeNodeId || !Array.isArray(allNodesData)) return null;
  for (const section of sortSections(allNodesData)) {
    for (const step of sortSteps(section.steps)) {
      if (step.tree_node_id === treeNodeId) return step;
    }
  }
  return null;
}

/**
 * @returns {{ selection_scope: string|null, steps: object[] }} Each step has `status` set per selection.
 */
export function buildTrainedToPayload(allNodesData, trainingCheckmarkSelection) {
  if (!trainingCheckmarkSelection || !Array.isArray(allNodesData)) {
    return { selection_scope: null, steps: [] };
  }

  if (trainingCheckmarkSelection.scope === "procedure") {
    const steps = [];
    for (const section of sortSections(allNodesData)) {
      for (const step of sortSteps(section.steps)) {
        const clone = deepCloneNode(step);
        sortNodeHierarchyOrders(clone);
        applyStepStatus(clone, STEP_STATUS.TRAINED);
        setIsCompleteDeep(clone, true);
        steps.push(clone);
      }
    }
    return {
      selection_scope: "procedure",
      is_complete: true,
      steps,
    };
  }

  if (
    trainingCheckmarkSelection.scope === "steps" &&
    trainingCheckmarkSelection.treeNodeIds?.length
  ) {
    const idSet = new Set(trainingCheckmarkSelection.treeNodeIds);
    const steps = [];
    for (const section of sortSections(allNodesData)) {
      for (const step of sortSteps(section.steps)) {
        const selected = idSet.has(step.tree_node_id);
        const clone = deepCloneNode(step);
        sortNodeHierarchyOrders(clone);
        if (selected) {
          setIsCompleteDeep(clone, true);
          applyStepStatus(clone, STEP_STATUS.PARTIALLY_TRAINED);
        } else {
          setIsCompleteDeep(clone, false);
          delete clone.status;
        }
        steps.push(clone);
      }
    }
    if (!steps.some((s) => s.is_complete === true)) {
      return { selection_scope: "steps", steps: [] };
    }
    return { selection_scope: "steps", steps };
  }

  if (trainingCheckmarkSelection.scope === "step" && trainingCheckmarkSelection.treeNodeId) {
    const targetId = trainingCheckmarkSelection.treeNodeId;
    const found = findStepByTreeNodeId(allNodesData, targetId);
    if (!found) {
      return { selection_scope: "step", steps: [] };
    }
    const steps = [];
    for (const section of sortSections(allNodesData)) {
      for (const step of sortSteps(section.steps)) {
        const selected = step.tree_node_id === targetId;
        const clone = deepCloneNode(step);
        sortNodeHierarchyOrders(clone);
        if (selected) {
          setIsCompleteDeep(clone, true);
          applyStepStatus(clone, STEP_STATUS.PARTIALLY_TRAINED);
        } else {
          setIsCompleteDeep(clone, false);
          delete clone.status;
        }
        steps.push(clone);
      }
    }
    return { selection_scope: "step", steps };
  }

  return { selection_scope: null, steps: [] };
}

/**
 * Top-level `trained_to.tree_node_id` for POST /training-log/create:
 * - Full procedure: procedure (revision) tree root.
 * - Steps: pass **the user's current selection** (not a union with previously trained steps), so the
 *   id matches the step(s) they actually checked — first of those in document order if multiple.
 */
function pickTreeNodeIdsInDocumentOrder(allNodesData, idArray) {
  const want = new Set(
    (idArray || [])
      .filter(Boolean)
      .map((id) => String(id).trim().toLowerCase()),
  );
  const out = [];
  if (!want.size || !Array.isArray(allNodesData)) return out;
  for (const section of sortSections(allNodesData)) {
    for (const step of sortSteps(section.steps)) {
      const id = step.tree_node_id;
      if (id == null || String(id).trim() === "") continue;
      if (want.has(String(id).trim().toLowerCase())) {
        out.push(String(id).trim());
      }
    }
  }
  return out;
}

export function resolveTrainedToOuterTreeNodeId({
  procedureTreeNodeId,
  trainingCheckmarkSelection,
  allNodesData,
}) {
  const fallback = procedureTreeNodeId
    ? String(procedureTreeNodeId).trim()
    : "";
  if (!trainingCheckmarkSelection || !Array.isArray(allNodesData)) {
    return fallback;
  }
  const scope = trainingCheckmarkSelection.scope;
  if (scope === "procedure") {
    return fallback;
  }
  if (scope === "step" && trainingCheckmarkSelection.treeNodeId) {
    return String(trainingCheckmarkSelection.treeNodeId).trim();
  }
  if (scope === "steps" && trainingCheckmarkSelection.treeNodeIds?.length) {
    const ordered = pickTreeNodeIdsInDocumentOrder(
      allNodesData,
      trainingCheckmarkSelection.treeNodeIds,
    );
    if (ordered.length) return ordered[0];
  }
  return fallback;
}

/** Top-level step `tree_node_id`s in document order (non-deleted steps only). */
function collectAllStepTreeNodeIdsInDocumentOrder(allNodesData) {
  const out = [];
  if (!Array.isArray(allNodesData)) return out;
  for (const section of sortSections(allNodesData)) {
    for (const step of sortSteps(section.steps)) {
      if (step?.is_deleted) continue;
      const id = step.tree_node_id;
      if (id == null || String(id).trim() === "") continue;
      out.push(String(id).trim());
    }
  }
  return out;
}

function isTreeNodeIdInTrainedSet(treeNodeId, trainedStepTreeNodeIdsSet) {
  if (
    !trainedStepTreeNodeIdsSet ||
    !(trainedStepTreeNodeIdsSet instanceof Set) ||
    trainedStepTreeNodeIdsSet.size === 0
  ) {
    return false;
  }
  const norm = String(treeNodeId).trim().toLowerCase();
  for (const x of trainedStepTreeNodeIdsSet) {
    if (String(x).trim().toLowerCase() === norm) return true;
  }
  return false;
}

/**
 * Top-level `tree_node_id` **array** for POST /training-log/create (below `trainer_signature`).
 * - Full revision: revision root + step ids that are **not** already trained (omit trained steps).
 * - One step: that step’s id only.
 * - Multiple steps: selected step ids not already trained, document order.
 */
export function buildCreateTrainingLogTreeNodeIdArray({
  procedureTreeNodeId,
  trainingCheckmarkSelection,
  allNodesData,
  trainedStepTreeNodeIdsSet,
}) {
  const revision = procedureTreeNodeId
    ? String(procedureTreeNodeId).trim()
    : "";

  if (!trainingCheckmarkSelection || !Array.isArray(allNodesData)) {
    return revision ? [revision] : [];
  }

  const scope = trainingCheckmarkSelection.scope;

  if (scope === "procedure") {
    const allSteps = collectAllStepTreeNodeIdsInDocumentOrder(allNodesData);
    const untrained = allSteps.filter(
      (id) => !isTreeNodeIdInTrainedSet(id, trainedStepTreeNodeIdsSet),
    );
    if (revision) {
      return [revision, ...untrained];
    }
    return untrained;
  }

  if (scope === "step" && trainingCheckmarkSelection.treeNodeId) {
    return [String(trainingCheckmarkSelection.treeNodeId).trim()];
  }

  if (scope === "steps" && trainingCheckmarkSelection.treeNodeIds?.length) {
    const selectedNotTrained = (trainingCheckmarkSelection.treeNodeIds || [])
      .map((id) => String(id).trim())
      .filter(Boolean)
      .filter((id) => !isTreeNodeIdInTrainedSet(id, trainedStepTreeNodeIdsSet));
    return pickTreeNodeIdsInDocumentOrder(allNodesData, selectedNotTrained);
  }

  return [];
}
